<template>
    <div
        class="card-border input-grid coral-coral-background p-2 input-amount-container ion-activatable"
        :class="{ web }"
        @click.stop.prevent
    >
        <button-minus
            class="round-button tint-button amount-button"
            :color="ION_VARIANT.CORAL"
            @mousedown.stop="appResult ? undefined : changeValue('minus')"
            @mouseup="removeInterval"
            @touchstart.stop="appResult ? changeValue('minus') : undefined"
            @touchend="removeInterval"
            @contextmenu.stop.prevent
        />

        <div class="input-col center-y">
            <input
                ref="amountInput"
                class="light-light-background max-size center ion-text-center remove-padding-input amount-input amount-input-fix"
                v-model.number="inputValue"
                :min="props.min"
                :max="max"
                id="input-amount-cart"
                :step="String(amountStep)"
                inputmode="numeric"
                type="text"
                :disabled="directInputDisabled || disabled"
                @click.stop="selectValue($event)"
                @focusin="handleFocus">
        </div>

        <button-plus
            class="round-button tint-button amount-button"
            :color="ION_VARIANT.CORAL"
            @mousedown.stop="appResult ? undefined : changeValue('plus')"
            @mouseup="removeInterval"
            @touchstart.stop="appResult ? changeValue('plus') : undefined"
            @touchend="removeInterval"
            @contextmenu.stop.prevent
        />
    </div>
</template>

<script setup lang="ts">
import { InputCustomEvent, IonInput } from "@ionic/vue";
import { computed, ref, watch, onMounted, onBeforeUnmount } from "vue";

import ButtonMinus from "@/components/button/ButtonMinus.vue";
import ButtonPlus from "@/components/button/ButtonPlus.vue";

import { INITIAL_TIMER, TIMER_MIN, TIMER_SUBSTRACTION } from "@/assets/variables";

import { ION_VARIANT } from "@/types/enum/ionicEnum";
import { InputAmountPerformanceProps } from "@/types/props";
import { InputAmountEmits } from "@/types/emits";
import { appResult } from "@/functions/enviroment";

const props = withDefaults(defineProps<InputAmountPerformanceProps>(), {
    min: 0,
    amountStep: 1
})
const emit = defineEmits<InputAmountEmits>()

const amountInput = ref()

let intervalId: ReturnType<typeof setInterval>;
let changeTimer = INITIAL_TIMER;

let preventBlur = false

const inputValue = computed<number>({
    get: () => {
        if (props.modelValue === undefined) return 0
        return Number(props.modelValue)
    },
    set: async (value) => {
        // const convertedNumber = Number(value)
		const convertedNumber = Number(String(value).replace(/[^0-9]*/g, ''));

        if (isNaN(convertedNumber)) {
            emit('update:modelValue', props.min)
            amountInput.value.value = props.min;
            return
        }

        if (convertedNumber < props.min) {
            emit('update:modelValue', props.min)
            amountInput.value.value = props.min;
            return
        }

        if (props.max && convertedNumber > props.max) {
            emit('update:modelValue', props.max)
            amountInput.value.value = props.max;
            return
        }

        emit('update:modelValue', convertedNumber)
        emit('change', convertedNumber)
        amountInput.value.value = convertedNumber;
    },
})

const handleFocus = (event: FocusEvent) => {
    if (preventBlur) {
        preventBlur = false
        return
    }
    const target = event.target as HTMLInputElement;
    target?.blur()
}

const selectValue = (event: Event): void => {
    const target = event.target as HTMLInputElement;
    preventBlur = true
    target.select();
}

const removeInterval = () => {
    clearInterval(intervalId)
    changeTimer = INITIAL_TIMER
}

const setUpdateInterval = (modifier: 'plus' | 'minus') => {
    clearInterval(intervalId)
    changeTimer = changeTimer - TIMER_SUBSTRACTION >= TIMER_MIN ? changeTimer - TIMER_SUBSTRACTION : TIMER_MIN
    intervalId = setTimeout(() => {
        inputValue.value += modifier === 'plus' ? props.amountStep : (-1 * props.amountStep)
        setUpdateInterval(modifier)
    }, changeTimer)
}

watch(props, () => {
    if (Number(props.modelValue) < 1) removeInterval()
})

const changeValue = (modifier: 'plus' | 'minus'): void => {
    inputValue.value += modifier === 'plus' ? props.amountStep : (-1 * props.amountStep)

    setUpdateInterval(modifier)
}

onMounted(() => {
  document.addEventListener('mouseup', removeInterval)
})

onBeforeUnmount(() => {
  document.removeEventListener('mouseup', removeInterval)
})
</script>

<style scoped>
.input-grid {
    display: grid;
    grid-template-columns: max-content 1fr max-content;
    gap: 5px;
}

.input-grid.web {
    max-height: 75px;
    align-content: center;
}

.input-grid.web > .amount-button {
    --dimension: 40px;
    width: var(--dimension) !important;
    height: var(--dimension) !important;
    min-width: var(--dimension) !important;
    min-height: var(--dimension) !important;
}

.amount-button {
    width: 45px !important;
    height: 45px !important;
    min-height: 45px !important;
    min-width: 45px !important;
    --border-radius: 50%;
    border-radius: 50%;
}

.amount-input {
    width: 12ch;
    border-radius: 16px;
    --border-radius: 16px;
    --placeholder-color: var(--ion-color-grey1);
    --highlight-color-focused: none;
    min-height: 38px;
    max-width: 60px;
    max-height: 38px;
}

.amount-input:disabled {
    color: var(--ion-color-grey1);
    cursor: not-allowed;
    opacity: unset; /* ios bug fix */
}

.amount-input.small {
    width: 7ch;
}

.reduced.small {
    width: 4ch;
    border-radius: 10px;
    --border-radius: 10px;
}

.amount-button.slim {
    height: 30px !important;
    width: 30px !important;
    min-height: 30px !important;
    min-width: 30px !important;
}

.amount-input.slim {
    min-height: 30px;
}

.input-amount-container.slim {
    min-width: 180px;
    max-width: 180px;
}

.input-amount-container.slim > ion-row > ion-col:not(.input-col) {
    max-width: 40px;
}

.amount-input-fix {
    border: none;
    outline: none;
    font-size: 1rem;
    width: 60px;
    max-height: 44px;
    color: var(--ion-color-coral);
}

@media (max-width: 395px) {
    .amount-button {
        width: 35px !important;
        height: 35px !important;
        min-height: 35px !important;
        min-width: 35px !important;
    }

    .amount-input {
        width: 10ch;
    }
}

</style>
